import { useStore } from '../core/store'
import { frameGap, owedFrame, setFrameWaker } from './frameBus'

/**
 * When the canvas draws.
 *
 * The loop used to draw on every animation frame for the life of the page: at
 * rest, under reduced motion, and behind the Research Lab, where the study counted
 * 25.6 frames a second painting a graph nobody could see. The field is never still
 * of its own accord — the seed breathes, signals travel along confident strands —
 * so "draw only when something changed" cannot be the whole rule. What can be is
 * a rate for each state the page is in:
 *
 *   FULL  something is live, growing or moving — a pass streaming, a run moving,
 *         a replay playing, the camera easing or a look pending, the layout
 *         settling, a strand growing, a pulse, fold or split mid-flight — or a
 *         person touched the page within `REST_AFTER_INPUT_MS`, or a change
 *         landed within `WAKE_TAIL_MS`. Every frame, exactly as before.
 *   REST  none of that: `REST_FPS`. The ambient motion carries on, drawn a third
 *         as often, which is plenty for a breath with a four-second period and a
 *         signal that takes several seconds to cross its strand.
 *   HOLD  a sheet covers the stage, or reduced motion is asked for and nothing is
 *         changing: nothing is drawn and the last frame stays up.
 *
 * One frame is OWED even through a hold, because "the last frame stays up" is
 * only true of a canvas that has one: the loop's first, the first after a resize
 * (a resized canvas is a cleared one), and the first after the theme changes
 * (`repaint`). Both canvases are opaque (`alpha: false`), so a canvas with no
 * frame is black, and every sheet's scrim is only a dim over it. In the dark
 * theme that black is a shade off the field and nobody saw it; on paper it was
 * a black slab behind the first-run sheet — the first thing a light-theme
 * visitor ever saw — and behind Settings the moment somebody picked Light, the
 * field went on showing the night theme through the sheet they picked it in.
 *
 * What moves the loop out of REST or HOLD is a WAKE, never a poll: a store write,
 * What moves the loop out of REST or HOLD is a WAKE, never a poll: a store write,
 * an input anywhere on the page, a resize, the sheet lifting, the motion
 * preference flipping, the tab coming back, or a frame subscriber asking for one
 * (`requestFrame` in `frameBus.ts`). The store is subscribed whole rather than
 * field by field on purpose: a list of the fields the canvas reads is a list that
 * goes stale the first time somebody adds one, and the cost of the broad reading —
 * a frame for a write that changed nothing on the canvas — is only paid when
 * something wrote, which a page at rest does not do.
 *
 * No setting, and nothing a reader does gets slower: an input puts the loop on
 * FULL before the frame that answers it is drawn.
 */

/** How long the full rate outlives the last input. The hold that keeps a hover,
 *  a pan or a stretch on every frame, and then lets go. */
export const REST_AFTER_INPUT_MS = 3000

/**
 * How long the full rate outlives a change nobody's hand made — a pass landing, a
 * sheet lifting, a card mounting.
 *
 * Long enough for the stylesheet's slowest transition (`--dur-slow`, 380 ms) and a
 * caption's touchdown fade to finish on real frames, and for the caption layer's
 * idle pass (every 100 ms) to run at least once after the DOM has settled. Under
 * reduced motion this is the ONLY tail, which is what "draw on change" comes to:
 * the change, and the few frames that show where it landed.
 */
export const WAKE_TAIL_MS = 400

/** The rate at rest. */
export const REST_FPS = 20
const REST_FRAME_MS = 1000 / REST_FPS
/**
 * A rested frame's timer fires this much early. The frame itself waits for the
 * next vsync, so a timer set for the whole interval lands one vsync late and the
 * rate falls to fifteen; half a 60 Hz frame early lands it on the right one.
 */
const VSYNC_SLACK_MS = 8

export type Cadence = 'full' | 'rest' | 'hold'

export interface CadenceState {
  /** A sheet covers the stage. */
  covered: boolean
  /** Something drawn is live, growing or moving. See the header. */
  live: boolean
  /** `prefers-reduced-motion: reduce`. */
  calm: boolean
  /** Milliseconds since the last input. */
  sinceInput: number
  /** Milliseconds since the last wake of any kind. */
  sinceWake: number
}

/** The whole policy, as a pure function of the state. */
export function cadenceFor(c: CadenceState): Cadence {
  // First, above even a live pass: a covered canvas is one nobody can see, and
  // everything it would have drawn is a pure function of the clock, so the first
  // frame after the sheet lifts shows exactly where it all got to.
  if (c.covered) return 'hold'
  if (c.live || c.sinceWake < WAKE_TAIL_MS) return 'full'
  if (c.calm) return 'hold'
  if (c.sinceInput < REST_AFTER_INPUT_MS) return 'full'
  return 'rest'
}

/** The browser's clock and frame primitives, injectable so the scheduler can be
 *  driven by a test. */
export interface CadenceEnv {
  raf: (cb: (t: number) => void) => number
  cancelRaf: (id: number) => void
  setTimer: (cb: () => void, ms: number) => number
  clearTimer: (id: number) => void
  now: () => number
}

export interface CadenceLoop {
  /** Something changed that may need drawing. */
  wake: () => void
  /** A person touched the page: a wake, and the input hold. */
  input: () => void
  /** The picture itself is gone or wrong — the canvas was cleared, or the
   *  palette changed — so the next frame is drawn even through a hold. One
   *  frame, not a rate: the hold resumes after it. */
  repaint: () => void
  stop: () => void
}

/**
 * The scheduler. `draw` paints one frame and answers whether anything it drew is
 * still live; the answer, the two predicates and the clocks decide when the next
 * one comes. `draw` is also told whether the gap before it was deliberate — the
 * frame was rested or held for — which is what `frameGap` publishes, and whether
 * the frame is an owed one, which `owedFrame` publishes.
 *
 * At most one frame or one rest timer is ever pending. A wake with a frame already
 * pending changes nothing; a wake with a rest timer pending brings the frame
 * forward to the next vsync.
 */
export function startCadence(
  draw: (t: number, afterGap: boolean, owed: boolean) => boolean,
  state: { covered: () => boolean; calm: () => boolean },
  env: CadenceEnv,
): CadenceLoop {
  let raf = 0
  let timer = 0
  let stopped = false
  let inputAt = -Infinity
  let wokeAt = -Infinity
  let drawnAt = -Infinity
  /** The last decision was to rest or to hold, so the next frame follows a gap
   *  the loop chose. */
  let gap = false
  /** The next frame is drawn even through a hold — see the header. True from
   *  the start, because a canvas that has never been drawn has no frame to keep
   *  up; a stage covered at mount (the first-run sheet) is the case it exists
   *  for. */
  let owed = true

  const request = () => {
    if (raf || stopped) return
    if (timer) {
      env.clearTimer(timer)
      timer = 0
    }
    raf = env.raf(frame)
  }

  const schedule = (live: boolean) => {
    if (stopped || raf || timer) return
    const now = env.now()
    const next = cadenceFor({
      covered: state.covered(),
      live,
      calm: state.calm(),
      sinceInput: now - inputAt,
      sinceWake: now - wokeAt,
    })
    gap = next !== 'full'
    if (next === 'full') {
      raf = env.raf(frame)
    } else if (next === 'rest') {
      const wait = Math.max(0, REST_FRAME_MS - (now - drawnAt) - VSYNC_SLACK_MS)
      timer = env.setTimer(() => {
        timer = 0
        request()
      }, wait)
    }
    // 'hold': nothing is pending, and only a wake brings the next frame.
  }

  function frame(t: number) {
    raf = 0
    if (stopped || (state.covered() && !owed)) return
    const repainting = owed
    owed = false
    // Scheduled in a `finally` so a frame that throws keeps the loop alive, as the
    // old unconditional loop did — and counts as live, so a failing draw is
    // retried on the next frame rather than parked until something else wakes it.
    let live = true
    try {
      live = draw(t, gap, repainting)
      drawnAt = env.now()
    } finally {
      schedule(live)
    }
  }

  return {
    wake() {
      wokeAt = env.now()
      request()
    },
    input() {
      inputAt = wokeAt = env.now()
      request()
    },
    repaint() {
      owed = true
      wokeAt = env.now()
      request()
    },
    stop() {
      stopped = true
      if (raf) env.cancelRaf(raf)
      if (timer) env.clearTimer(timer)
      raf = timer = 0
    },
  }
}

/** Anything a person does to the page. Pointer events cover touch and pen. */
const INPUTS = ['pointerdown', 'pointermove', 'wheel', 'keydown'] as const

/**
 * Run a canvas's frame loop under the cadence, wired to every wake the page has.
 * Returns the teardown. Shared by the flat and the spatial canvas, which differ
 * in what a frame does and in nothing about when one is due.
 *
 * "A sheet covers the stage" is read off `main.stage`'s `inert`, which is the one
 * flag every sheet already sets through `useModalFocus` — the Lab, export, the
 * first-run sheet, the library, sources, search, the palette, the shortcuts — so a
 * sheet added tomorrow holds the canvas without being listed anywhere here.
 */
export function runCanvasLoop(host: HTMLElement, draw: (t: number) => boolean): () => void {
  const stage = host.closest<HTMLElement>('main.stage')
  const mq = window.matchMedia('(prefers-reduced-motion: reduce)')
  const loop = startCadence(
    (t, afterGap, owed) => {
      frameGap.deliberate = afterGap
      owedFrame.now = owed
      return draw(t)
    },
    { covered: () => stage?.inert === true, calm: () => mq.matches },
    {
      raf: (cb) => requestAnimationFrame(cb),
      cancelRaf: (id) => cancelAnimationFrame(id),
      setTimer: (cb, ms) => window.setTimeout(cb, ms),
      clearTimer: (id) => window.clearTimeout(id),
      now: () => performance.now(),
    },
  )
  const onInput = () => loop.input()
  const onWake = () => loop.wake()
  const onRepaint = () => loop.repaint()
  const listen = { capture: true, passive: true } as const
  for (const type of INPUTS) window.addEventListener(type, onInput, listen)
  mq.addEventListener('change', onWake)
  document.addEventListener('visibilitychange', onWake)
  const unsubscribe = useStore.subscribe(onWake)
  const unregister = setFrameWaker(onWake, onRepaint)
  const inert = stage ? new MutationObserver(onWake) : null
  if (stage) inert!.observe(stage, { attributes: true, attributeFilter: ['inert'] })
  loop.wake()

  return () => {
    loop.stop()
    for (const type of INPUTS) window.removeEventListener(type, onInput, listen)
    mq.removeEventListener('change', onWake)
    document.removeEventListener('visibilitychange', onWake)
    unsubscribe()
    unregister()
    inert?.disconnect()
  }
}
